import { produce } from 'immer'

// =============================================================================
// Redux slice for the agent-creation wizard (AVR-435). Holds only state that
// is shared beyond a single wizard mount: the org's Cartesia tenants, the
// list of voice agents (for the resume affordance), and per-key loading/error
// flags. The wizard's in-flight form values stay in local component state —
// the thunks return their results so the page drives step advancement.
// =============================================================================

// Action Types
export const SET_LOADING = 'agentProvisioning/setLoading'
export const SET_ERROR = 'agentProvisioning/setError'
export const SET_TENANTS = 'agentProvisioning/setTenants'
export const SET_AGENTS = 'agentProvisioning/setAgents'
export const SET_CARTESIA_AGENTS = 'agentProvisioning/setCartesiaAgents'
export const UPSERT_AGENT = 'agentProvisioning/upsertAgent'

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setTenants = (payload) => ({ type: SET_TENANTS, payload })
export const setAgents = (payload) => ({ type: SET_AGENTS, payload })
export const setCartesiaAgents = (payload) => ({ type: SET_CARTESIA_AGENTS, payload })
export const upsertAgent = (payload) => ({ type: UPSERT_AGENT, payload })

// Initial state
export const initialState = {
  // Cartesia tenants for the currently-targeted org. `orgId` records which org
  // the items belong to so a stale render after an org switch is detectable.
  tenants: { items: [], orgId: null },

  // Voice-agent master rows for the targeted org, each carrying its
  // voice_agent_configs so the UI can derive provisioned-vs-incomplete.
  agents: { items: [], orgId: null },

  // The selected tenant's Cartesia agents (by name) for the pick-by-name step.
  // `tenantId` records which tenant the items belong to (staleness guard).
  cartesiaAgents: { items: [], tenantId: null },

  loading: {
    createOrganization: false,
    createAgentUser: false,
    tenants: false,
    createTenant: false,
    agents: false,
    cartesiaAgents: false,
    validateAgent: false,
    provision: false,
    renameAgent: false,
    changeSlug: false,
  },

  error: {
    createOrganization: null,
    createAgentUser: null,
    tenants: null,
    createTenant: null,
    agents: null,
    cartesiaAgents: null,
    validateAgent: null,
    provision: null,
    renameAgent: null,
    changeSlug: null,
  },
}

// Reducer
export default function agentProvisioningReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_LOADING:
        newState.loading = { ...newState.loading, ...payload }
        break
      case SET_ERROR:
        newState.error = { ...newState.error, ...payload }
        break
      case SET_TENANTS:
        newState.tenants = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          orgId: payload?.orgId ?? null,
        }
        break
      case SET_AGENTS:
        newState.agents = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          orgId: payload?.orgId ?? null,
        }
        break
      // Replace one roster row in place (a rename, AVR-827). The BE returns
      // the same full shape `GET /voice-agents` does, so swapping the whole
      // entry keeps the card fields (title, direction, campaign_count) rather
      // than merging a partial over them. A row we don't hold is ignored — the
      // list reloads on its own, and inserting an agent the org filter never
      // selected would be worse than missing one.
      case UPSERT_AGENT: {
        const index = newState.agents.items.findIndex((agent) => agent.id === payload?.id)
        if (index !== -1) {
          newState.agents.items[index] = payload
        }
        break
      }
      case SET_CARTESIA_AGENTS:
        newState.cartesiaAgents = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          tenantId: payload?.tenantId ?? null,
        }
        break
      default:
        break
    }
  })
}
